import { dev } from '$app/environment'
import type { User } from '@supabase/supabase-js'
import type { Page } from '@sveltejs/kit'
import { derived, type Readable } from 'svelte/store'

export const INVALID_ORG_CODES = [
  'about',
  'pricing',
  'home',
  'pausewithme',
  'support',
  'signup',
  'sign-up',
  'login',
  'log-in',
]

export const activeSessionCookieName = 'active-session' as const

export type UsernameGetter = {
  (user: User): string
  (user: string): string
}

export const getUserName: UsernameGetter = (user: User | string) => {
  const email = typeof user === 'string' ? user : user.email
  return email ? email.substring(0, email.indexOf('@')) : 'Unknown User'
}

const isSessionUidRedirect = (redirect: unknown): redirect is SessionUid =>
  !!redirect &&
  typeof redirect === 'object' &&
  Object.hasOwn(redirect, 'sessionUid')

type SessionUid = {
  sessionUid: string | undefined
}

type Redirect = SessionUid | Path | URL | undefined | null

type GetUrlOptions = {
  /**
   * - `true`: default and uses localhost in dev mode, or pausly.app in production
   * - `false`: no domain will be used, and only the path will be used.
   * - `'production'`: the url will use the pausly.app domain, even in local
   *   development.
   */
  domain?: 'production' | boolean
  /** Any search parameters that should be added to the url */
  search?: Record<string, string> | undefined
  /**
   * If provided, a `redirect` search parameter will be added to the URL. This
   * parameter is used to redirect the user after signing in or finishing the
   * registration.
   *
   * If this is an array, then the first one that is not undefined or null will
   * be used. This is for convenience so you don't have to determine the best
   * redirect option beforehand.
   *
   * **NOTE**: Only paths are allowed here, not fully quantified URLs. If you
   * use a `URL` object here, beware that everything but the path will be
   * stripped away. You cannot use an external URL.
   */
  redirect?: Redirect | Redirect[] | undefined
}

export type Path = `/${string}`

export const isPath = (path: unknown): path is Path =>
  typeof path === 'string' && path.startsWith('/')

export const getUrl = (
  path: Path,
  { domain = true, search, redirect }: GetUrlOptions | undefined = {},
): string => {
  let url: string = path

  if (domain) {
    url =
      domain === 'production' || !dev
        ? `https://www.pausly.app${path}`
        : `http://localhost:5173${path}`
  }

  // Make sure we copy it
  search = search ? { ...search } : {}

  if (redirect) {
    let actualRedirect: string | undefined = undefined
    if (!Array.isArray(redirect)) redirect = [redirect]
    for (const redirectOption of redirect) {
      if (redirectOption) {
        if (isSessionUidRedirect(redirectOption)) {
          if (redirectOption.sessionUid) {
            actualRedirect = `/session/${redirectOption.sessionUid}`
          }
        } else if (redirectOption instanceof URL) {
          actualRedirect = redirectOption.pathname + redirectOption.search
        } else {
          actualRedirect = redirectOption
        }
      }
      if (actualRedirect) {
        search.redirect = actualRedirect
        break
      }
    }
  }

  if (Object.keys(search).length > 0) {
    url += `?${new URLSearchParams(search).toString()}`
  }

  return url
}

/**
 * Returns a readable store of the login url, that contains a `redirect`
 * parameter if applicable.
 */
export const getLoginUrl = (page: Readable<Page>) => {
  const isOnAccountPage = (url: URL) => {
    if (url.pathname === '/') return true

    for (const path of ['/login', '/register', '/reset-password']) {
      if (url.pathname.startsWith(path)) {
        return true
      }
    }
    return false
  }

  return derived(page, ($page) => {
    return isOnAccountPage($page.url)
      ? '/login'
      : `/login?redirect=${encodePathComponent($page.url.pathname)}`
  })
}

export const getHelpPath = (fullSlug: string | null): Path =>
  `/help/${fullSlug ?? ''}${typeof window !== 'undefined' && window.location.search.includes('preview') ? '?preview' : ''}` as Path

export function getOrganizationUrl(
  code: string,
  options?: GetUrlOptions,
): string
export function getOrganizationUrl(
  code: string,
  path: Path,
  options?: GetUrlOptions,
): string

export function getOrganizationUrl(
  code: string,
  pathOrOptions?: Path | GetUrlOptions,
  options?: GetUrlOptions,
) {
  let path: Path | undefined = undefined
  if (isPath(pathOrOptions)) {
    path = pathOrOptions
  } else {
    options = pathOrOptions
  }
  return getUrl(`/@${code}${path ?? ''}`, options)
}

/**
 * After a successful login or registration, this function determines where the
 * user should be redirected to.
 *
 * This is the order in which the redirect is determined:
 *
 * 1. A redirect param in the URL
 * 2. If there is an active session id stored as a cookie, then the session URL
 * 3. The fallback
 */
export const determineRedirectPath = ({
  url,
  activeSessionUid,
  fallback,
}: {
  url?: URL | string | undefined | null
  activeSessionUid?: string | undefined
  fallback: Path
}): Path => {
  if (url) {
    if (typeof url === 'string') url = new URL(url)
    if (url.searchParams.has('redirect')) {
      return url.searchParams.get('redirect') as Path
    }
  }
  if (activeSessionUid) return `/session/${activeSessionUid}`

  return fallback
}

export const encodePathComponent = (path: string) =>
  encodeURIComponent(path).replaceAll('%2F', '/')

/** Returns a number between 0 (incl) and 1 (excl). */
type RandomNumberGenerator = () => number

/** Based on mulberry32 */
export const seededRandomGenerator =
  (seed: number): RandomNumberGenerator =>
  (): number => {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
